import Add from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import { AccountType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useEffect, useMemo, useState } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import AutocompleteRutterCustomersTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteRutterCustomersTypeahead';
import AutocompleteRutterVendorsTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteRutterVendorsTypeahead';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import { Account } from '~hooks/useAccount/models';
import { AddressItem } from '~hooks/useAddress/models/AddressItem';
import useCursorPagination from '~hooks/useCursorPagination/useCursorPagination';
import { RutterMappedRecord } from '~hooks/useRutter/models';
import useRutter from '~hooks/useRutter/useRutter';
import CustomerAccountForm from '~pages/Settings/Accounts/CustomerAccountForm/CustomerAccountForm';
import VendorAccountFormDialogWrapper from '~pages/Settings/Accounts/VendorAccountForm/VendorAccountFormDialogWrapper';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { MAPPING_COLUMN_MIN_WIDTH_PX } from './mappingColumnWidth';
import { useQuickBooksMappingQueries } from './QuickBooksMappingQueryContext';
import QuickBooksTypeaheadCell from './QuickBooksTypeaheadCell';

const I18N_PREFIX = 'administration.integration.rutter.quick_books_online';

function formatAddress(address: Nullable<AddressItem>) {
  if (!address) {
    return '';
  }

  return [
    address.streetAddress,
    address.premise,
    address.locality,
    address.administrativeArea,
    address.postalCode,
  ]
    .filter(Boolean)
    .join(', ');
}

interface MappedAccount {
  account: Account;
  mappedRecord: Nullable<RutterMappedRecord>;
}

interface AccountMappingRow extends MappedAccount {
  id: string;
}

interface AccountMappingTabProps {
  accountType: AccountType.CUSTOMER | AccountType.VENDOR;
}

export default function AccountMappingTab({ accountType }: AccountMappingTabProps) {
  const isCustomerTab = accountType === AccountType.CUSTOMER;
  const { connectionId, isLoading, saveAccountMapping } = useQuickBooksMappingQueries();
  const { toasterStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { getCustomerAccountsPage, getVendorAccountsPage } = useRutter();

  const [searchValue, setSearchValue] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const {
    goToPage,
    isFetching,
    pagination,
    reloadCurrentPage,
    startOver,
    rows: mappedAccounts,
  } = useCursorPagination<MappedAccount>({
    fetchPage: ({ cursor, limit, link, signal }) =>
      (isCustomerTab ? getCustomerAccountsPage : getVendorAccountsPage)({
        connectionId: String(connectionId),
        cursor,
        limit,
        link,
        search: searchValue,
        signal,
      }),
    limit: DEFAULT_API_PAGE_SIZE,
  });

  useEffect(() => {
    if (connectionId) {
      void startOver();
    }
  }, [accountType, connectionId, searchValue]);

  const rows: AccountMappingRow[] = useMemo(
    () =>
      mappedAccounts.map((mappedAccount) => ({
        ...mappedAccount,
        id: mappedAccount.account.id,
      })),
    [mappedAccounts],
  );

  const closeCreateDialog = () => setIsCreateDialogOpen(false);

  const handleSave = async (
    row: AccountMappingRow,
    nextQuickBooksRecordId: Nullable<string>,
  ) => {
    const saved = await saveAccountMapping(
      accountType,
      row.account.id,
      row.mappedRecord?.id ?? null,
      nextQuickBooksRecordId,
    );

    if (saved) {
      toasterStore.push(
        alert(
          t(`${I18N_PREFIX}.grid.tread_name_updated`, { name: row.account.name }),
          AlertTypes.success,
        ),
      );
    }

    await reloadCurrentPage();

    return saved;
  };

  const columns: Array<DataTableColumn<AccountMappingRow>> = useMemo(() => {
    return [
      {
        field: 'name',
        headerName: t(
          isCustomerTab
            ? `${I18N_PREFIX}.tread_first.columns.tread_customer`
            : `${I18N_PREFIX}.tread_first.columns.tread_vendor`,
        ),
        valueGetter: (row) => row.account.name,
      },
      {
        field: 'address',
        headerName: t(
          isCustomerTab
            ? `${I18N_PREFIX}.tread_first.columns.billing_address`
            : `${I18N_PREFIX}.tread_first.columns.remit_to_address`,
        ),
        valueGetter: (row) => {
          return formatAddress(
            isCustomerTab ? row.account.billingAddress : row.account.address,
          );
        },
      },
      {
        field: 'mapping',
        headerName: t(
          isCustomerTab
            ? `${I18N_PREFIX}.tread_first.columns.quick_books_customer`
            : `${I18N_PREFIX}.tread_first.columns.quick_books_vendor`,
        ),
        minWidth: MAPPING_COLUMN_MIN_WIDTH_PX,
        renderCell: (row) => (
          <QuickBooksTypeaheadCell
            dataTestId={`quick-books-mapping-select-${row.account.id}`}
            onSave={(nextQuickBooksRecordId) => handleSave(row, nextQuickBooksRecordId)}
            value={row.mappedRecord}
            renderTypeahead={(typeaheadProps) =>
              isCustomerTab ? (
                <AutocompleteRutterCustomersTypeahead
                  {...typeaheadProps}
                  companyId={companyId}
                  connectionId={String(connectionId)}
                />
              ) : (
                <AutocompleteRutterVendorsTypeahead
                  {...typeaheadProps}
                  companyId={companyId}
                  connectionId={String(connectionId)}
                />
              )
            }
          />
        ),
      },
    ];
  }, [companyId, connectionId, isCustomerTab]);

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <SmallButton
          color="brandV2Yellow"
          onClick={() => setIsCreateDialogOpen(true)}
          startIcon={<Add />}
        >
          {t(
            isCustomerTab
              ? `${I18N_PREFIX}.tread_first.new_customer`
              : `${I18N_PREFIX}.tread_first.new_vendor`,
          )}
        </SmallButton>
      </Box>

      <DataTable
        columns={columns}
        headerTrailingComponent={
          <DataGridPager
            isFetching={isFetching}
            onNavigate={(link) => void goToPage(link)}
            pagination={pagination}
          />
        }
        id={`quick-books-mapping-${accountType}-grid`}
        loading={isLoading || isFetching}
        onSearchChange={setSearchValue}
        rows={rows}
      />

      {isCustomerTab ? (
        <CustomerAccountForm
          isOpen={isCreateDialogOpen}
          onAccountCreated={() => {
            setIsCreateDialogOpen(false);
            void goToPage();
          }}
          onClose={closeCreateDialog}
        />
      ) : (
        <VendorAccountFormDialogWrapper
          isOpen={isCreateDialogOpen}
          onAccountCreated={() => {
            setIsCreateDialogOpen(false);
            void goToPage();
          }}
          onClose={closeCreateDialog}
        />
      )}
    </>
  );
}
